/**
 * Self-serve signup (ADR-0041) — a 3-step wizard served on the reserved `app.<base>`
 * host: (1) account (company + subdomain + admin credentials), (2) billing/anagrafica
 * with the Italian e-invoicing rules, (3) plan choice. The backend provisions a
 * tenant, persists the billing into `tenants.profile`, assigns the chosen plan, and
 * hands back the subdomain to redirect into. NO payment / NO email verification yet.
 *
 * E-invoicing rule (owner): in Italy a private individual uses Codice Destinatario
 * `0000000`, a foreign buyer (private OR company) uses `XXXXXXX`; only an Italian
 * business carries a real SDI channel code / PEC. The UI only SHOWS those fields when
 * they matter — the backend (`normalized_sdi_code`) is the source of truth.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Loader2, Check, X } from "lucide-react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  onboardingApi,
  onboardingBaseDomain,
  OnboardingApiError,
  type OnboardingPlan,
} from "@/lib/api-onboarding";
import {
  type BillingPeriod,
  formatAnnualTotal,
  formatGb,
  formatPrice,
  hasAnnual,
} from "@/lib/api-billing";
import { cn } from "@/lib/cn";
import {
  type BillingForm,
  EMPTY_BILLING,
  billingComplete,
  toBillingPayload,
} from "@/lib/billingProfile";
import { BillingFieldset } from "@/components/billing/BillingFieldset";

const SLUG_DEBOUNCE_MS = 300;
/** Mirror of the backend slug rule (`provisioning._SCHEMA_RE`): a Postgres
 * identifier — lowercase letter first, then letters/digits/underscore. Hyphens
 * are NOT allowed (the schema name is `t_<slug>`). */
const SLUG_RE = /^[a-z][a-z0-9_]*$/;

type SlugState =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "available" }
  | { kind: "unavailable"; reason: string | null };

/** Map a backend `tenancy:slug_*` reason code to a friendly i18n key. */
function slugReasonKey(reason: string | null): string {
  if (!reason) return "onboarding.slugTaken";
  if (reason.includes("exists")) return "onboarding.slugTaken";
  if (reason.includes("pending")) return "onboarding.slugPending";
  if (reason.includes("reserved")) return "onboarding.slugReserved";
  return "onboarding.slugInvalid";
}

export function OnboardingSignupPage() {
  const { t } = useTranslation();
  const base = onboardingBaseDomain();

  const [step, setStep] = React.useState(0); // 0 = account, 1 = billing, 2 = plan
  const [company, setCompany] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [billing, setBilling] = React.useState<BillingForm>(EMPTY_BILLING);
  const [slugState, setSlugState] = React.useState<SlugState>({ kind: "idle" });
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [redirecting, setRedirecting] = React.useState(false);

  const setB = <K extends keyof BillingForm>(key: K, value: BillingForm[K]) =>
    setBilling((b) => ({ ...b, [key]: value }));

  // Plan selection (ADR-0041): load the public plans + let the visitor pick one +
  // a cadence. Pre-Stripe the chosen plan is assigned immediately + free.
  const [plans, setPlans] = React.useState<OnboardingPlan[]>([]);
  const [planKey, setPlanKey] = React.useState<string | null>(null);
  const [period, setPeriod] = React.useState<BillingPeriod>("month");
  React.useEffect(() => {
    let alive = true;
    onboardingApi
      .plans()
      .then((ps) => {
        if (!alive) return;
        setPlans(ps);
        const cheapest = [...ps].sort((a, b) => (a.price_cents ?? 0) - (b.price_cents ?? 0))[0];
        if (cheapest) setPlanKey(cheapest.key);
      })
      .catch(() => {
        /* No plans configured → signup without a subscription (UNLIMITED). */
      });
    return () => {
      alive = false;
    };
  }, []);
  const anyAnnual = plans.some((p) => p.price_year_cents != null);

  // Live slug availability — debounced, race-guarded.
  const reqIdRef = React.useRef(0);
  React.useEffect(() => {
    const value = slug.trim();
    if (!value) {
      setSlugState({ kind: "idle" });
      return;
    }
    if (!SLUG_RE.test(value)) {
      setSlugState({ kind: "unavailable", reason: "tenancy:slug_invalid" });
      return;
    }
    setSlugState({ kind: "checking" });
    const id = ++reqIdRef.current;
    const handle = window.setTimeout(async () => {
      try {
        const res = await onboardingApi.checkSlug(value);
        if (id !== reqIdRef.current) return;
        setSlugState(
          res.available ? { kind: "available" } : { kind: "unavailable", reason: res.reason },
        );
      } catch {
        if (id !== reqIdRef.current) return;
        setSlugState({ kind: "idle" });
      }
    }, SLUG_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [slug]);

  //: An invoiceable profile is now REQUIRED to sign up (owner, 2026-07-28) — the rule
  //: itself lives in `lib/billingProfile`, mirrored from the backend that refuses the sale.
  const billingValid = billingComplete(billing);
  const [billingTouched, setBillingTouched] = React.useState(false);

  const accountValid =
    company.trim().length > 0 &&
    slugState.kind === "available" &&
    email.trim().length > 2 &&
    password.length >= 8;
  const planValid = plans.length === 0 || planKey !== null;
  const canSubmit = !submitting && accountValid && billingValid && planValid;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (step === 1 && !billingValid) {
      // Named the moment they try to move on, not on a pristine form.
      setBillingTouched(true);
      return;
    }
    if (step < 2) {
      setStep((s) => s + 1);
      return;
    }
    setError(null);
    setSubmitting(true);
    const plan = plans.find((p) => p.key === planKey);
    const effPeriod: BillingPeriod =
      period === "year" && plan && hasAnnual(plan) ? "year" : "month";
    const body = {
      company_name: company.trim(),
      slug: slug.trim(),
      admin_email: email.trim(),
      admin_password: password,
      plan_key: planKey,
      billing_period: effPeriod,
      billing: toBillingPayload(billing),
    };
    try {
      // Pay-then-provision, ALWAYS (ADR-0079): the workspace is created by the payment,
      // so we leave the SPA for Stripe's page. There is no free fallback — a deployment
      // with no billing configured sells nothing, and says so.
      const res = await onboardingApi.checkout(body);
      setRedirecting(true);
      window.location.href = res.checkout_url;
    } catch (err) {
      if (err instanceof OnboardingApiError) {
        if (err.status === 409 && err.message.includes("email_has_account")) {
          // A 409 is USUALLY the slug — this one is the email (ADR-0078: the free door
          // refuses an address that already has a Tray Account). Marking the slug
          // unavailable here would send them off to rename a workspace that is fine.
          setError(t("onboarding.emailHasAccount"));
          setStep(0);
        } else if (err.status === 409) {
          setSlugState({ kind: "unavailable", reason: err.message });
          // Say WHICH conflict, exactly like the live check does — the reason codes are
          // already distinct and so are the messages. Hard-coding `slugTaken` here made a
          // slug merely HELD by a checkout in progress read as permanently taken, sending
          // the visitor off to rename a workspace that was fine (live, 2026-07-28).
          setError(t(slugReasonKey(err.message)));
          setStep(0); // back to the account step so they can fix the slug
        } else if (err.status === 429) {
          setError(t("onboarding.errorThrottled"));
        } else if (err.message.includes("billing_disabled")) {
          setError(
            t(
              "onboarding.billingDisabled",
              "Le registrazioni non sono disponibili su questa installazione.",
            ),
          );
        } else {
          setError(err.message || t("onboarding.errorGeneric"));
        }
      } else {
        setError(t("onboarding.errorGeneric"));
      }
      setSubmitting(false);
    }
  }

  const steps = [
    t("onboarding.steps.account", "Account"),
    t("onboarding.steps.billing", "Fatturazione"),
    t("onboarding.steps.plan", "Piano"),
  ];

  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <div className="w-full max-w-md rounded-xl border border-border/60 bg-surface p-8 shadow-lg">
        <div className="mb-6 flex items-center gap-2.5">
          <img src="/tray-logo.svg" alt="" aria-hidden="true" className="h-7 w-7 shrink-0" />
          <span className="text-base font-semibold tracking-tight">Tray</span>
        </div>

        <h1 className="text-lg font-semibold text-foreground">{t("onboarding.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.subtitle")}</p>

        {redirecting ? (
          <div className="mt-8 flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("onboarding.redirecting")}
          </div>
        ) : (
          <>
            <Stepper steps={steps} current={step} />
            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              {step === 0 && (
                <AccountStep
                  t={t}
                  base={base}
                  company={company}
                  setCompany={setCompany}
                  slug={slug}
                  setSlug={setSlug}
                  slugState={slugState}
                  email={email}
                  setEmail={setEmail}
                  password={password}
                  setPassword={setPassword}
                />
              )}

              {step === 1 && (
                <BillingFieldset
                  t={t}
                  billing={billing}
                  setB={setB}
                  showErrors={billingTouched}
                />
              )}

              {step === 2 && plans.length > 0 && (
                <PlanStep
                  t={t}
                  plans={plans}
                  planKey={planKey}
                  setPlanKey={setPlanKey}
                  period={period}
                  setPeriod={setPeriod}
                  anyAnnual={anyAnnual}
                />
              )}
              {step === 2 && plans.length === 0 && (
                <p className="py-2 text-sm text-muted-foreground">
                  {t("onboarding.noPlans", "Nessun piano da scegliere — inizi subito gratis.")}
                </p>
              )}

              {error && (
                <Alert tone="danger" title={t("onboarding.errorTitle")}>
                  {error}
                </Alert>
              )}

              <div className="flex items-center gap-2 pt-1">
                {step > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep((s) => s - 1)}
                    disabled={submitting}
                  >
                    {t("onboarding.back", "Indietro")}
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={step === 0 ? !accountValid : step === 2 ? !canSubmit : false}
                  className="flex-1"
                >
                  {step < 2
                    ? t("onboarding.next", "Avanti")
                    : submitting
                      ? t("onboarding.creating")
                      : t("onboarding.submit")}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/** The 3-step progress header. */
function Stepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <ol className="mt-5 flex items-center gap-2">
      {steps.map((label, i) => (
        <li key={label} className="flex flex-1 items-center gap-2">
          <span
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
              i < current
                ? "bg-primary text-primary-foreground"
                : i === current
                  ? "bg-primary/15 text-primary ring-1 ring-primary"
                  : "bg-muted text-muted-foreground",
            )}
          >
            {i < current ? <Check className="h-3.5 w-3.5" /> : i + 1}
          </span>
          <span
            className={cn(
              "truncate text-xs font-medium",
              i === current ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {label}
          </span>
        </li>
      ))}
    </ol>
  );
}

function AccountStep({
  t,
  base,
  company,
  setCompany,
  slug,
  setSlug,
  slugState,
  email,
  setEmail,
  password,
  setPassword,
}: {
  t: TFunction;
  base: string;
  company: string;
  setCompany: (v: string) => void;
  slug: string;
  setSlug: (v: string) => void;
  slugState: SlugState;
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
}) {
  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="company">{t("onboarding.company")}</Label>
        <Input
          id="company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder={t("onboarding.companyPlaceholder")}
          autoComplete="organization"
          required
          autoFocus
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="slug">{t("onboarding.slug")}</Label>
        <div className="flex items-stretch overflow-hidden rounded-md border border-border focus-within:ring-2 focus-within:ring-ring">
          <input
            id="slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, ""))}
            className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm outline-none"
            placeholder="acme"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            required
          />
          <span className="flex items-center bg-muted px-2.5 text-sm text-muted-foreground">
            .{base}
          </span>
        </div>
        <SlugStatus state={slugState} t={t} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="email">{t("onboarding.email")}</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password">{t("onboarding.password")}</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
        <p className="text-xs text-muted-foreground">{t("onboarding.passwordHelp")}</p>
      </div>
    </>
  );
}

function PlanStep({
  t,
  plans,
  planKey,
  setPlanKey,
  period,
  setPeriod,
  anyAnnual,
}: {
  t: TFunction;
  plans: OnboardingPlan[];
  planKey: string | null;
  setPlanKey: (k: string) => void;
  period: BillingPeriod;
  setPeriod: (p: BillingPeriod) => void;
  anyAnnual: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{t("onboarding.choosePlan", "Scegli un piano")}</Label>
        {anyAnnual && (
          <div className="inline-flex rounded-lg border border-border/70 bg-surface/40 p-0.5 text-xs">
            {(["month", "year"] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={cn(
                  "rounded-md px-2.5 py-1 font-medium transition-colors",
                  period === p
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {p === "month" ? t("billing.monthly", "Mensile") : t("billing.annual", "Annuale")}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-2">
        {plans.map((p) => {
          const eff: BillingPeriod = period === "year" && hasAnnual(p) ? "year" : "month";
          const annual = eff === "year" ? formatAnnualTotal(p, t) : null;
          const features = Object.entries(p.features).filter(([, on]) => on);
          const limits = Object.entries(p.limits);
          return (
            <button
              type="button"
              key={p.key}
              onClick={() => setPlanKey(p.key)}
              className={cn(
                "block w-full rounded-lg border p-3 text-left transition-colors",
                planKey === p.key
                  ? "border-primary bg-primary/[0.05]"
                  : "border-border/70 hover:border-border",
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground">{p.name}</div>
                  {p.description && (
                    <div className="text-xs text-muted-foreground">{p.description}</div>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-sm font-semibold tabular-nums text-foreground">
                    {formatPrice(p, t, eff)}
                  </div>
                  {annual && <div className="text-[11px] text-primary/80">{annual}</div>}
                </div>
              </div>

              {features.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {features.map(([k]) => (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] text-foreground"
                    >
                      <Check className="h-3 w-3 text-primary" />
                      {t(`billing.axis.${k}`, k)}
                    </span>
                  ))}
                </div>
              )}

              {limits.length > 0 && (
                <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-border/50 pt-2.5 text-[11px]">
                  {limits.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-2">
                      <dt className="truncate text-muted-foreground">
                        {t(`billing.axis.${k}`, k)}
                      </dt>
                      <dd className="shrink-0 font-medium tabular-nums text-foreground">
                        {v === null
                          ? "∞"
                          : k === "max_storage_gb"
                            ? formatGb(v)
                            : v.toLocaleString()}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The per-slug availability line under the subdomain field. */
function SlugStatus({ state, t }: { state: SlugState; t: TFunction }) {
  if (state.kind === "idle") return null;
  if (state.kind === "checking") {
    return (
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        {t("onboarding.slugChecking")}
      </p>
    );
  }
  if (state.kind === "available") {
    return (
      <p className="flex items-center gap-1.5 text-xs text-success">
        <Check className="h-3 w-3" />
        {t("onboarding.slugAvailable")}
      </p>
    );
  }
  return (
    <p className="flex items-center gap-1.5 text-xs text-danger">
      <X className="h-3 w-3" />
      {t(slugReasonKey(state.reason))}
    </p>
  );
}
